/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TEventsMap, TMap } from './types';
import useImperativeComponentContext from './useImperativeIdentifierContext';
import uniqueId from 'lodash-es/uniqueId';

const useImperativeComponentEvents = <Events extends TMap = TMap>(
  handlers: Partial<TEventsMap<Events>>,
) => {
  const uniqueHookId = useMemo(() => uniqueId('hook'), []);

  const { id, eventsStore } = useImperativeComponentContext<Events>();
  const hasRegistered = useRef(false);

  const registerEvents = useCallback(() => {
    Object.entries(handlers).forEach(([name, callback]) => {
      if (!eventsStore[id]) eventsStore[id] = {};
      if (!eventsStore[id][name])
        eventsStore[id][name as keyof (typeof eventsStore)[typeof id]] = [];

      eventsStore[id][name as keyof (typeof eventsStore)[typeof id]]?.push({
        cb: callback as (args?: Events[keyof Events] | undefined) => unknown,
        uniqueHookId: uniqueHookId,
      });
    });
  }, [eventsStore, handlers, id, uniqueHookId]);

  useEffect(() => {
    registerEvents();
    return () => {
      hasRegistered.current = false;
      Object.entries(handlers).forEach(([name]) => {
        eventsStore[id][name as keyof (typeof eventsStore)[typeof id]] = (
          eventsStore[id][name as keyof (typeof eventsStore)[typeof id]] ?? []
        ).filter((current) => current.uniqueHookId !== uniqueHookId);
      });
    };
  }, [eventsStore, handlers, id, registerEvents, uniqueHookId]);

  return uniqueHookId;
};

export default useImperativeComponentEvents;
